class StoreAvailabilityButton extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('click', this.showDrawer.bind(this));
  }

  showDrawer() {
    const sidePanel = document.getElementById('StoreAvailabilityDrawer');
    sidePanel.toggleAttribute('open', true);
    if (this.dataset.maximumDistance && !sidePanel.hasAttribute('data-maximum-distance')) {
      sidePanel.dataset.maximumDistance = this.dataset.maximumDistance;
    }

    // Focus close button for accessibility
    const closeButton = sidePanel.querySelector('.side-panel-close');
    setTimeout(() => {
       if (closeButton) {
        closeButton.focus();
      }
    }, 500);
  }
}

customElements.define('store-availability-button', StoreAvailabilityButton);

class StoreAvailabilityDrawer extends HTMLElement {
  constructor() {
    super();
    this.focusTrapHandler = this.trapFocus.bind(this);
  }

  connectedCallback() {
    this.cc = document.querySelector('.click-capture');
    
    this.sizeSelectorButton = this.querySelector('#size_selector_button')
    this.sizeSelectorButton.addEventListener('click', this.moveToNextPage.bind(this));

    this.sizeSelector = this.querySelector('#size_selector');
    this.sizeSelector.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', this.toggleSizeSelectorButton.bind(this));
    });

    this.zipForm = this.querySelector('#zip_form');
    this.zipForm.addEventListener('submit', this.handleSubmitZipCode.bind(this));

    this.results = this.querySelector('.store-availability--results');

  }

  disconnectedCallback() {
    this.querySelector('#size_selector_button').removeEventListener('click', this.moveToNextPage.bind(this));
  }

  // Watch component attributes for changes
  static get observedAttributes() {
    return ['open', 'class'];
  }

  // React to attribute changes
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.hasAttribute('open') ? this.toggleDrawer(true) : this.toggleDrawer(false);
    }

    if (name === 'class') {
      if (oldValue && newValue && oldValue.includes('active') && !newValue.includes('active')) {
        this.toggleDrawer(false);
        this.results.toggleAttribute('hidden', true);
        this.results.querySelector('.store-availability--no-results').toggleAttribute('hidden', true);
      }
    }
  }

  /* Control layout, visibility, and accessibility */

  toggleDrawer(open = true) {
    document.body.classList.toggle('open-cc', open ? true : false);
    this.classList.toggle('active', open ? true : false);
    this.setAttribute('tabindex', open ? 0 : -1);

    if (!open) {
      this.toggleAttribute('open', false);
      this.disableFocusTrap();
    } else {
      this.enableFocusTrap();
    }

    this.showZipForm(false);
  }

  getFocusableElements() {
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[role="button"]:not([aria-disabled="true"])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])'
    ].join(',');

    return Array.from(this.querySelectorAll(focusableSelector)).filter((element) => {
      return element.offsetParent !== null && !element.closest('[hidden]');
    });
  }

  trapFocus(event) {
    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = this.getFocusableElements();
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (!firstFocusableElement || !lastFocusableElement) {
      event.preventDefault();
      return;
    }

    if (!this.contains(document.activeElement)) {
      event.preventDefault();
      firstFocusableElement.focus();
    } else if (event.shiftKey && document.activeElement === firstFocusableElement) {
      event.preventDefault();
      lastFocusableElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
      event.preventDefault();
      firstFocusableElement.focus();
    }
  }

  enableFocusTrap() {
    this.removeEventListener('keydown', this.focusTrapHandler);
    this.addEventListener('keydown', this.focusTrapHandler);
  }

  disableFocusTrap() {
    this.removeEventListener('keydown', this.focusTrapHandler);
  }

  moveToNextPage() {
    if (this.sizeSelectorButton.disabled) {
      return;
    }

    this.showZipForm(true);
  }

  showZipForm(show) {
    if (show) {
      this.sizeSelector.classList.remove('transition-visible');
      this.sizeSelector.classList.add('transition-hidden');

      this.zipForm.toggleAttribute('hidden', false);
      this.zipForm.classList.remove('transition-hidden');
      this.zipForm.classList.add('transition-visible');

      setTimeout(() => {
        this.sizeSelector.toggleAttribute('hidden', true);
      }, 300);

    } else {
      this.sizeSelector.toggleAttribute('hidden', false);
      this.sizeSelector.classList.remove('transition-hidden');
      this.sizeSelector.classList.add('transition-visible');

      this.zipForm.classList.remove('transition-visible');
      this.zipForm.classList.add('transition-hidden');

      setTimeout(() => {
        this.zipForm.toggleAttribute('hidden', true);
      }, 300);
    }
  }

  toggleSizeSelectorButton() {
    const atLeastOneChecked = Array.from(this.sizeSelector.querySelectorAll('input[type="checkbox"]')).some(checkbox => checkbox.checked);
    this.querySelector('#size_selector_button').toggleAttribute('disabled', !atLeastOneChecked);
  }

  /* Handle form submissions */

  handleSubmitZipCode(event) {
    event.preventDefault();
    const formData = new FormData(event.target);
    const zipCode = formData.get('zip_code');
    const productId = formData.get('product_id');

    const url = `${theme.routes.root_url}apps/integration/pickup/locations?product_id=${productId}&address=${zipCode}`;
    fetch(url).then(response => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      return response.json();
    }).then(data => {
      this.renderResponse(data);
    }).catch(error => {
      this.renderError(error);
    });
  }

  getSelectedSizes() {
    const selectedSizes = [];

    this.sizeSelector.querySelectorAll('input:checked').forEach(checkbox => {
      selectedSizes.push({ "size": checkbox.value, "variant_id": checkbox.dataset.variantId });
    });

    return selectedSizes;
  }

  formatAndSortLocations(locations, sizes) {
    const maximumDistance = this.dataset.maximumDistance ? Number(this.dataset.maximumDistance) : 100;
    const sizeIds = sizes.map(size => size.variant_id);
    const filteredLocations = locations.filter((location) => {
      if (location.distance != null && location.distance <= maximumDistance) {
        return location.variants.some(variant => sizeIds.includes(variant.id.toString()));
      } else {
        return false;
      }
    });

    return filteredLocations.sort((a, b) => a.distance - b.distance);
  }

  renderResponse(response) {
    const selectedSizes = this.getSelectedSizes();
    const locations = this.formatAndSortLocations(response.locations, selectedSizes);
    
    // Clear previous results
    const listContainer = this.querySelector('.store-availability--store-list');
    listContainer.innerHTML = '';

    this.results.querySelector('.store-availability--no-results').toggleAttribute('hidden', locations.length > 0);

    const template = this.querySelector('#store-list-template').content;

    // Warning message
    if (locations.length > 0) {
      listContainer.insertAdjacentHTML('afterbegin', `<p class="store-availability--warning"><strong>Store inventory may not reflect real-time availability.</strong></p>`);
    } 

    locations.forEach(location => {
      const clone = document.importNode(template, true);

      clone.querySelector('slot[name="store-city"]').textContent = location.address.city;
      clone.querySelector('b[slot="store-name"]').textContent = location.name;
      clone.querySelector('p[slot="store-address"]').innerHTML = location.address.formatted.splice(0, 3).join('<br />');
      clone.querySelector('slot[name="store-distance"]').textContent = location.distance ? `${location.distance.toFixed(2)} miles` : '';

      const availableSizesSlot = clone.querySelector('slot[name="store-available-sizes"]');
      availableSizesSlot.textContent = location.variants.map(variant => variant.title).join(', ');

      listContainer.appendChild(clone);
    });

    // Show results container
    this.results.toggleAttribute('hidden', false);
  }

  renderError(error) {
    this.results.querySelector('.store-availability--loading-error').removeAttribute('hidden');
    this.results.toggleAttribute('hidden', false);
  }
}


customElements.define('store-availability-drawer', StoreAvailabilityDrawer);
